/**
 * CapUtilizationWidgetPage — widget-style report for `cap-utilization-widget`.
 *
 * Renders a green utilization bar per cap snapshot from
 * GET /api/v1/cap-utilization-widget. Bars capped at 100% visually; true %
 * rendered in label. Filter controls for scope_type + cap_type.
 */
import { useState } from "react";
import { useCapUtilizationWidgetList } from "./hooks";
import type { CapUtilizationWidgetRow } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL =
	"text-[11px] font-medium uppercase tracking-wider text-zinc-500";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200";

const SCOPE_TYPES = ["", "offer", "campaign", "publisher"] as const;
const CAP_TYPES = ["", "daily", "monthly", "lifetime", "conversions", "revenue"] as const;

function fmtPct(pct: number | null): string {
	if (pct === null || !Number.isFinite(pct)) return "—";
	return `${pct.toFixed(1)}%`;
}

function fmtNum(n: number): string {
	return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtRange(start: number, end: number): string {
	const s = new Date(start * 1000).toISOString().slice(0, 10);
	const e = new Date(end * 1000).toISOString().slice(0, 10);
	return `${s} → ${e}`;
}

function UtilizationBar({ row }: { row: CapUtilizationWidgetRow }) {
	const pct = row.utilization_pct ?? 0;
	const width = Math.max(0, Math.min(100, pct));
	return (
		<div
			className="flex flex-col gap-1.5"
			data-testid={`cap-utilization-widget-row-${row.id}`}
		>
			<div className="flex items-baseline justify-between gap-4">
				<div className="flex flex-col">
					<span className="text-sm font-medium text-zinc-900">
						{row.scope_type}: {row.scope_id}
					</span>
					<span className="text-xs text-zinc-500">
						{row.cap_type} · {fmtRange(row.period_start, row.period_end)}
					</span>
				</div>
				<div className="flex flex-col items-end">
					<span
						className="text-sm font-semibold tabular-nums text-zinc-900"
						data-testid={`cap-utilization-widget-pct-${row.id}`}
					>
						{fmtPct(row.utilization_pct)}
					</span>
					<span className="text-xs tabular-nums text-zinc-500">
						{fmtNum(row.used_value)} / {fmtNum(row.cap_value)}
					</span>
				</div>
			</div>
			<div
				className="h-2.5 w-full overflow-hidden rounded-[4px] bg-zinc-100"
				aria-label="utilization bar"
				role="progressbar"
				aria-valuenow={Math.round(width)}
				aria-valuemin={0}
				aria-valuemax={100}
			>
				<div
					className="h-full rounded-[4px] bg-green-500 transition-[width] duration-200"
					style={{ width: `${width}%` }}
					data-testid={`cap-utilization-widget-bar-${row.id}`}
				/>
			</div>
		</div>
	);
}

export function CapUtilizationWidgetPage() {
	const [scopeType, setScopeType] = useState<string>("");
	const [capType, setCapType] = useState<string>("");

	const listQuery = useCapUtilizationWidgetList({
		sort: "utilization",
		scope_type: scopeType || undefined,
		cap_type: capType || undefined,
	});
	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="cap-utilization-widget-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Report
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Cap Utilization
				</h1>
				<p className="text-sm text-zinc-600">
					Snapshots of cap usage across offers, campaigns and publishers.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Cap utilization filters"
				data-testid="cap-utilization-widget-filters"
			>
				<div className="flex flex-wrap items-end gap-4">
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Scope type</span>
						<select
							className={SELECT}
							value={scopeType}
							onChange={(e) => setScopeType(e.target.value)}
							data-testid="cap-utilization-widget-filter-scope-type"
						>
							{SCOPE_TYPES.map((v) => (
								<option key={v || "all"} value={v}>
									{v || "All"}
								</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Cap type</span>
						<select
							className={SELECT}
							value={capType}
							onChange={(e) => setCapType(e.target.value)}
							data-testid="cap-utilization-widget-filter-cap-type"
						>
							{CAP_TYPES.map((v) => (
								<option key={v || "all"} value={v}>
									{v || "All"}
								</option>
							))}
						</select>
					</label>
					<div className="ml-auto text-right">
						<p className={LABEL}>Total</p>
						<p
							className="text-lg font-semibold tabular-nums text-zinc-900"
							data-testid="cap-utilization-widget-total"
						>
							{items.length}
						</p>
					</div>
				</div>
			</section>

			<section className={CARD} aria-label="Cap utilization report">
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="cap-utilization-widget-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="cap-utilization-widget-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="cap-utilization-widget-empty"
					>
						No cap snapshots yet.
					</p>
				) : (
					<div
						className="flex flex-col gap-5"
						data-testid="cap-utilization-widget-list"
					>
						{items.map((row) => (
							<UtilizationBar key={row.id} row={row} />
						))}
					</div>
				)}
			</section>
		</main>
	);
}

export default CapUtilizationWidgetPage;
